/**
 * Smooth Sales recently-viewed products (app-free).
 *
 * Loaded on every product page (see layout/theme.liquid) to record the
 * visit, and by sections/recently-viewed.liquid to render the row.
 * History is a list of product handles in localStorage; product data
 * (price, image, availability) is fetched from /products/<handle>.js at
 * render time and kept for five minutes in sessionStorage. Handles that 404
 * (deleted products) are pruned from the history.
 *
 * REQUEST VOLUME MATTERS HERE. The store rate-limits each shopper, and the
 * header search's suggestions share that allowance. This row used to fetch
 * eight products at once the moment the homepage loaded, so a returning
 * shopper who typed straight into the search bar found their suggestions
 * answered with 429s — "search doesn't work on the homepage, but does on
 * the search page", where there is no row. So the row (the last section on
 * the homepage) now waits until it is scrolled near, fetches one product at
 * a time, stops as soon as it has enough or the store says slow down, and
 * reuses what it fetched for five minutes.
 */

const STORAGE_KEY = 'smooth-sales:recently-viewed:v1';
const MAX_STORED = 12;
const CACHE_KEY = 'smooth-sales:recently-viewed:cache:v1';
const CACHE_TTL_MS = 5 * 60 * 1000;
const LOAD_AHEAD = '800px 0px';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function readHistory() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items.filter((handle) => typeof handle === 'string') : [];
  } catch (error) {
    return [];
  }
}

function writeHistory(handles) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
  } catch (error) {
    /* storage full or blocked — fail quietly */
  }
}

function readCache() {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    const cache = raw ? JSON.parse(raw) : {};
    return cache && typeof cache === 'object' ? cache : {};
  } catch (error) {
    return {};
  }
}

function writeCache(cache) {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    /* storage full or blocked — fail quietly */
  }
}

class RateLimitedError extends Error {}

/* Only the fields a card renders, so the cache stays small. */
function trimProduct(product) {
  return {
    title: product.title,
    vendor: product.vendor,
    url: product.url,
    featured_image: product.featured_image,
    price: product.price,
    compare_at_price: product.compare_at_price,
    available: product.available,
  };
}

/**
 * One product, from the session cache when fresh, otherwise the store.
 * Resolves { product } or { deleted: true }; throws RateLimitedError on 429.
 */
async function loadProduct(handle, cache) {
  const hit = cache[handle];
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.value;

  const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (response.status === 429) throw new RateLimitedError('HTTP 429');
  let value;
  if (response.status === 404) {
    value = { deleted: true };
  } else if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  } else {
    value = { product: trimProduct(await response.json()) };
  }
  cache[handle] = { at: Date.now(), value };
  return value;
}

function currentProductHandle() {
  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Record the visit when this module loads on a product page.
{
  const handle = currentProductHandle();
  if (handle) {
    const history = readHistory().filter((existing) => existing !== handle);
    history.unshift(handle);
    writeHistory(history);
  }
}

function formatMoney(cents) {
  return `$${(cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/* Mirrors the markup of snippets/price.liquid (card size) so the row's
 * prices pick up the same red-tag styling as every other product card. */
function buildPrice(product) {
  const onSale = typeof product.compare_at_price === 'number' && product.compare_at_price > product.price;

  const wrap = document.createElement('div');
  wrap.className = onSale ? 'ss-price ss-price--sale' : 'ss-price';

  if (onSale) {
    const dontPay = document.createElement('span');
    dontPay.className = 'ss-price__dont-pay';
    const label = document.createElement('span');
    label.setAttribute('aria-hidden', 'true');
    label.textContent = "DON'T PAY ";
    const compare = document.createElement('span');
    compare.className = 'ss-price__compare';
    compare.textContent = formatMoney(product.compare_at_price);
    dontPay.append(label, compare);
    wrap.appendChild(dontPay);
  }

  const tag = document.createElement('span');
  tag.className = 'ss-price__tag';

  const accessible = document.createElement('span');
  accessible.className = 'visually-hidden';
  accessible.textContent = formatMoney(product.price);

  const amount = document.createElement('span');
  amount.className = 'ss-price__amount';
  amount.setAttribute('aria-hidden', 'true');
  const [dollars, cents] = (product.price / 100).toFixed(2).split('.');
  amount.append(`$${Number(dollars).toLocaleString('en-AU')}`);
  const sup = document.createElement('sup');
  sup.className = 'ss-price__cents';
  sup.textContent = cents;
  amount.appendChild(sup);

  tag.append(accessible, amount);
  wrap.appendChild(tag);

  if (onSale) {
    const save = document.createElement('span');
    save.className = 'ss-price__save';
    save.textContent = `SAVE ${formatMoney(product.compare_at_price - product.price)}`;
    wrap.appendChild(save);
  }

  return wrap;
}

function resizeImage(url, size) {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', String(size));
    return parsed.toString();
  } catch (error) {
    return url;
  }
}

class RecentlyViewedProducts extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  #started = false;

  connectedCallback() {
    if (this.#started) return;

    // The section stays [hidden] until it has cards, and a hidden box never
    // intersects, so watch its (zero-height) section wrapper instead.
    const target = this.closest('.recently-viewed')?.parentElement || this;
    if (!('IntersectionObserver' in window)) {
      this.#start();
      return;
    }
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#stopWatching();
        this.#start();
      },
      { rootMargin: LOAD_AHEAD }
    );
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#stopWatching();
  }

  #stopWatching() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #start() {
    if (this.#started) return;
    this.#started = true;
    this.#load();
  }

  async #load() {
    const limit = Number(this.dataset.limit) || 5;
    const minItems = Number(this.dataset.minItems) || 1;
    const current = currentProductHandle();
    const handles = readHistory()
      .filter((handle) => handle !== current)
      .slice(0, limit + 3);

    // One at a time, and only until the row is full: usually `limit`
    // requests, never a burst. A 429 ends the run so the row never spends
    // allowance the shopper's search needs.
    const cache = readCache();
    const deleted = new Set();
    const products = [];
    let rateLimited = false;
    for (const handle of handles) {
      if (products.length >= limit) break;
      try {
        const value = await loadProduct(handle, cache);
        if (value.deleted) {
          deleted.add(handle);
        } else if (value.product && value.product.available) {
          products.push(value.product);
        }
      } catch (error) {
        if (error instanceof RateLimitedError) {
          rateLimited = true;
          break;
        }
      }
    }
    writeCache(cache);

    if (deleted.size > 0) {
      writeHistory(readHistory().filter((handle) => !deleted.has(handle)));
    }

    if (!this.isConnected) return;

    /* Enough history: show the viewed row. Otherwise a sparse one-card row
     * looks broken, so swap to "Last Chance" — random picks from the
     * fallback collection (viewed items excluded). If that fetch fails,
     * fall back to whatever history exists rather than nothing. */
    if (products.length >= minItems) {
      this.#render(products.slice(0, limit));
      return;
    }

    // Told to slow down: show what we have and make no further request.
    if (rateLimited) {
      if (products.length > 0) this.#render(products.slice(0, limit));
      return;
    }

    const fallback = await this.#fetchFallback(limit, new Set(handles));
    if (fallback.length >= Math.min(minItems, limit)) {
      const heading = this.closest('.recently-viewed')?.querySelector('[data-rv-heading]');
      if (heading && this.dataset.fallbackHeading) heading.textContent = this.dataset.fallbackHeading;
      this.#render(fallback.slice(0, limit));
      return;
    }

    if (products.length > 0) this.#render(products.slice(0, limit));
  }

  #render(products) {
    if (products.length === 0) return;
    this.replaceChildren(...products.map((product) => this.#buildCard(product)));
    this.closest('.recently-viewed')?.removeAttribute('hidden');
  }

  async #fetchFallback(limit, excludeHandles) {
    const handle = (this.dataset.fallbackCollection || '').trim();
    if (handle === '') return [];
    try {
      const response = await fetch(
        `${root}collections/${encodeURIComponent(handle)}/products.json?limit=50`,
        { headers: { Accept: 'application/json' } }
      );
      if (!response.ok) return [];
      const data = await response.json();
      const candidates = (data.products || [])
        .filter(
          (product) =>
            product.images?.length > 0 &&
            product.variants?.some((variant) => variant.available) &&
            !excludeHandles.has(product.handle)
        )
        .map((product) => {
          const variant = product.variants.find((entry) => entry.available) || product.variants[0];
          return {
            title: product.title,
            vendor: product.vendor,
            url: `${root}products/${product.handle}`,
            featured_image: product.images[0].src,
            price: Math.round(parseFloat(variant.price) * 100),
            compare_at_price: variant.compare_at_price
              ? Math.round(parseFloat(variant.compare_at_price) * 100)
              : null,
            available: true,
          };
        });

      /* Fisher–Yates shuffle for a genuinely random selection */
      for (let i = candidates.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
      }
      return candidates.slice(0, limit);
    } catch (error) {
      return [];
    }
  }

  #buildCard(product) {
    const card = document.createElement('div');
    card.className = 'recently-viewed__card';

    const media = document.createElement('a');
    media.className = 'recently-viewed__media';
    media.href = product.url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'recently-viewed__image';
      image.src = resizeImage(product.featured_image, 480);
      image.alt = '';
      image.loading = 'lazy';
      image.width = 480;
      image.height = 480;
      media.appendChild(image);
    }
    card.appendChild(media);

    const info = document.createElement('div');
    info.className = 'recently-viewed__info';

    if (product.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'recently-viewed__vendor';
      vendor.textContent = product.vendor;
      info.appendChild(vendor);
    }

    const title = document.createElement('a');
    title.className = 'recently-viewed__title';
    title.href = product.url;
    title.textContent = product.title;
    info.appendChild(title);

    info.appendChild(buildPrice(product));
    card.appendChild(info);

    return card;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
